/**
 * Unified State Management — Extended from Designer's Store
 * Adds: dot-path keys, Proxy bridge for Studio's direct state mutation
 */

class Store {
  constructor() {
    this.state = {
      // Current mode
      mode: 'create',

      // Auth (shared)
      auth: { token: null, customer: null, showLoginModal: false },

      // UI
      ui: { loading: false, error: null, toasts: [] },

      // Create mode (Studio state shape)
      creation: {
        intentCard: null,
        visionAnalysis: null,
        visionLoading: false,
        secondImageUrl: null,
        generating: false,
        result: null,
      },

      // Gallery
      gallery: { images: [], page: 1, total: 0, loading: false, hasMore: true },
      favorites: { images: [], page: 1, total: 0, loading: false, hasMore: true },
      uploads: { images: [], page: 1, total: 0, loading: false, hasMore: true },
      // Mine (H-135a gallery L2 "mine" tab): the signed-in customer's own
      // generated assets (create-runtime /api/customer/assets).
      mine: { images: [], total: 0, loading: false, hasMore: false },
      galleryTab: 'gallery',
      favoriteIds: new Set(),

      // L2 subtabs (H-135a): active sub-tab id per mode — { [mode]: tabId }.
      // Written by core/subtabs.js; synced to the URL as ?mode=X&tab=Y.
      subtabs: {},

      // Image workflow
      sourceImage: null,
      selectedImage: null,
      aspectRatio: '3:4',

      // Inspiration cards
      inspirationCards: {
        loading: false,
        cards: [],
        activeIndex: 0,
        checkedCards: {},
        // Per-card strength (ADR-005 2.5.0 — weighted cards). { id: 'low'|'mid'|'high' };
        // absent = 'mid' (verbatim). Sent to generate as card_strengths for checked cards.
        cardStrengths: {},
        isDefaultCards: false,
      },

      // Credits
      credits: { balance: 0, dailyFreeUsed: 0, dailyFreeRemaining: 10, dailyFreeLimit: 10, streak: 0, dailyLoginClaimed: false },

      // Design mode (Designer state shape)
      design: {
        currentProduct: null,
        currentVariant: null,
        currentFace: null,
        isSDKReady: false,
        isSDKLoaded: false,
        sdkInstance: null,
        materials: [],
        loading: false,
        injectImageUrl: null,
      },

      // Upload queue
      uploadQueue: [],

      // Community
      community: { loading: false, endProducts: [], featured: [], trending: [], activeTab: 'trending' },
    }

    this.listeners = new Map()
  }

  /**
   * Subscribe to state changes. Supports dot-path keys: 'creation.intentCard'
   * @param {string} key
   * @param {Function} callback (newValue, oldValue) => void
   * @returns {Function} unsubscribe
   */
  subscribe(key, callback) {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set())
    }
    this.listeners.get(key).add(callback)
    return () => {
      const cbs = this.listeners.get(key)
      if (cbs) cbs.delete(callback)
    }
  }

  /**
   * Update state. Supports dot-path: setState('creation.intentCard', card)
   */
  setState(key, value) {
    const parts = key.split('.')
    if (parts.length === 1) {
      const old = this.state[key]
      this.state[key] = value
      this._notify(key, value, old)
    } else {
      // Dot-path: navigate to parent, set leaf
      let obj = this.state
      for (let i = 0; i < parts.length - 1; i++) {
        obj = obj[parts[i]]
        if (!obj) return
      }
      const leaf = parts[parts.length - 1]
      const old = obj[leaf]
      obj[leaf] = value
      // Notify both the full path and the top-level key
      this._notify(key, value, old)
      this._notify(parts[0], this.state[parts[0]], undefined)
    }
  }

  /**
   * Get state value by dot-path
   */
  getState(key) {
    const parts = key.split('.')
    let obj = this.state
    for (const part of parts) {
      if (obj == null) return undefined
      obj = obj[part]
    }
    return obj
  }

  setStates(updates) {
    Object.entries(updates).forEach(([key, value]) => this.setState(key, value))
  }

  getAllState() {
    return { ...this.state }
  }

  reset(key) {
    if (key) {
      this.setState(key, null)
    } else {
      Object.keys(this.state).forEach(k => this.setState(k, null))
    }
  }

  _notify(key, newVal, oldVal) {
    const cbs = this.listeners.get(key)
    if (cbs) {
      cbs.forEach(cb => {
        try { cb(newVal, oldVal) }
        catch (e) { console.error(`[Store] Error in listener for "${key}":`, e) }
      })
    }
  }

  clearListeners() {
    this.listeners.clear()
  }
}

export const store = new Store()
export { Store }
export default store
